import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import type { ActivityLog, Membership, AiEstimateItem, TermsAndConditions, CreateTermsAndConditionsPayload, UpdateTermsAndConditionsPayload, PlatformAnnouncement } from '@/types';

// Export types for use in components
export type { ActivityLog };
export type { Membership as MyOrganizationMembership };
export type { AiEstimateItem };
export type { TermsAndConditions };
export type { CreateTermsAndConditionsPayload };
export type { UpdateTermsAndConditionsPayload };

// ===== AUTH HOOKS =====
export function useLogin() {
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data } = await api.post('/auth/login', credentials);
      return data;
    },
    onSuccess: (data) => {
      login(data.token, data.refreshToken, data.user);
      navigate('/');
    },
  });
}

export function useGoogleLogin() {
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);
  return useMutation({
    mutationFn: async ({ idToken }: { idToken: string }) => {
      const { data } = await api.post('/auth/oauth/google', { idToken });
      return data;
    },
    onSuccess: (data) => {
      login(data.token, data.refreshToken, data.user);
      navigate('/');
    },
  });
}

export function useRegister() {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/auth/register', payload);
      return data.data;
    },
    onSuccess: () => {
      navigate('/confirm-account');
    },
  });
}

export function useResendVerification() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (email: string) => {
      await api.post('/auth/resend-verification', { email });
    },
    onSuccess: () => {
      toast.success(t('auth.verificationResent'));
    },
  });
}

// ===== BANK HOOKS =====
export function useBankAccounts() {
  return useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const { data } = await api.get('/bank/accounts');
      return data.data;
    },
  });
}

export function useBankTransactions(accountId: string, params = {}) {
  return useQuery({
    queryKey: ['bankTransactions', accountId, params],
    queryFn: async () => {
      const { data } = await api.get(`/bank/accounts/${accountId}/transactions`, { params });
      return data;
    },
    enabled: !!accountId,
  });
}

export function useAutoReconcile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/bank/reconcile');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankTransactions'] });
    },
  });
}

export function useBankConnections() {
  return useQuery({
    queryKey: ['bankConnections'],
    queryFn: async () => {
      const { data } = await api.get('/bank/connections');
      return data.data;
    },
  });
}

export function useAuthorizeBankConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/bank/connections/authorize');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankConnections'] });
    },
  });
}

export function useSyncBankConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { data } = await api.post(`/bank/connections/${connectionId}/sync`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankConnections'] });
      qc.invalidateQueries({ queryKey: ['bankTransactions'] });
    },
  });
}

export function useDeleteBankConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) => {
      await api.delete(`/bank/connections/${connectionId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankConnections'] });
    },
  });
}

export function useCompleteBankConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code: string; state: string }) => {
      const { data } = await api.post('/bank/connections/complete', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankConnections'] });
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
    },
  });
}

// ===== CLIENT HOOKS =====
export function useClients(params = {}) {
  return useQuery({
    queryKey: ['clients', params],
    queryFn: async () => {
      const { data } = await api.get('/clients', { params });
      return data;
    },
  });
}

export function useClient(id: string) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: async () => {
      const { data } = await api.get(`/clients/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useCreateClient() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/clients', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success(t('clients.created'));
    },
  });
}

export function useUpdateClient() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      const { data } = await api.put(`/clients/${id}`, payload);
      return data.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', variables.id] });
      toast.success(t('clients.updated'));
    },
  });
}

export function useDeleteClient() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/clients/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success(t('clients.deleted'));
      navigate('/clients');
    },
  });
}

export function useKboLookup() {
  return useMutation({
    mutationFn: async (vat: string) => {
      const { data } = await api.get(`/kbo/lookup/${vat}`);
      return data.data;
    },
  });
}

// ===== INVOICE HOOKS =====
export function useInvoices(params = {}) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: async () => {
      const { data } = await api.get('/invoices', { params });
      return data;
    },
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const { data } = await api.get(`/invoices/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/invoices', payload);
      return data.data;
    },
    onSuccess: (invoice) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.success(t('invoices.created'));
      navigate(`/invoices/${invoice.id}`);
    },
  });
}

export function useUpdateInvoice() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      const { data } = await api.put(`/invoices/${id}`, payload);
      return data.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', variables.id] });
      toast.success(t('invoices.updated'));
    },
  });
}

export function useUpdateInvoiceStatus() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data } = await api.patch(`/invoices/${id}/status`, { status });
      return data.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', variables.id] });
      toast.success(t('invoices.statusUpdated'));
    },
  });
}

export function useRecordPayment() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      const { data } = await api.post(`/invoices/${id}/payments`, payload);
      return data.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', variables.id] });
      toast.success(t('invoices.paymentRecorded'));
    },
  });
}

export function useCreateCreditNote() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      const { data } = await api.post(`/invoices/${id}/credit-note`, payload);
      return data.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', variables.id] });
      toast.success(t('invoices.creditNoteCreated'));
    },
  });
}

export function useCancelInvoice() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/invoices/${id}/cancel`);
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.success(t('invoices.cancelled'));
    },
  });
}

export function useIssueInvoice() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/invoices/${id}/issue`);
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.success(t('invoices.issued'));
    },
  });
}

export function useSendInvoiceEmail() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      await api.post(`/invoices/${id}/send-email`, payload);
    },
    onSuccess: () => {
      toast.success(t('invoices.emailSent'));
    },
  });
}

export function useSendInvoiceViaPeppol() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/invoices/${id}/send-peppol`);
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.success(t('invoices.peppolSent'));
    },
  });
}

export async function downloadInvoicePdf(id: string, filename: string = 'invoice.pdf') {
  const { setIsDownloading } = useUIStore.getState();
  try {
    setIsDownloading(true, 'Generating PDF... 0%');
    const { data } = await api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setIsDownloading(true, `Generating PDF... ${percentage}%`);
        }
      },
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsDownloading(false);
  } catch (err) {
    setIsDownloading(false);
    throw err;
  }
}

export async function openInvoicePdfForPrint(id: string) {
  const { setIsDownloading } = useUIStore.getState();
  try {
    setIsDownloading(true, 'Generating PDF... 0%');
    const { data } = await api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setIsDownloading(true, `Generating PDF... ${percentage}%`);
        }
      },
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const printWindow = window.open(url);
    printWindow?.addEventListener('load', () => {
      setIsDownloading(false);
      printWindow.print();
    });
  } catch (err) {
    setIsDownloading(false);
    throw err;
  }
}

export async function downloadCreditNotePdf(id: string, cnId: string, filename: string = 'credit-note.pdf') {
  const { setIsDownloading } = useUIStore.getState();
  try {
    setIsDownloading(true, 'Generating PDF... 0%');
    const { data } = await api.get(`/invoices/${id}/credit-notes/${cnId}/pdf`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setIsDownloading(true, `Generating PDF... ${percentage}%`);
        }
      },
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsDownloading(false);
  } catch (err) {
    setIsDownloading(false);
    throw err;
  }
}

export async function openCreditNotePdfForPrint(id: string, cnId: string) {
  const { setIsDownloading } = useUIStore.getState();
  try {
    setIsDownloading(true, 'Generating PDF... 0%');
    const { data } = await api.get(`/invoices/${id}/credit-notes/${cnId}/pdf`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setIsDownloading(true, `Generating PDF... ${percentage}%`);
        }
      },
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const printWindow = window.open(url);
    printWindow?.addEventListener('load', () => {
      setIsDownloading(false);
      printWindow.print();
    });
  } catch (err) {
    setIsDownloading(false);
    throw err;
  }
}

// ===== QUOTE HOOKS =====
export function useQuotes(params = {}) {
  return useQuery({
    queryKey: ['quotes', params],
    queryFn: async () => {
      const { data } = await api.get('/quotes', { params });
      return data;
    },
  });
}

export function useQuote(id: string) {
  return useQuery({
    queryKey: ['quote', id],
    queryFn: async () => {
      const { data } = await api.get(`/quotes/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useCreateQuote() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/quotes', payload);
      return data.data;
    },
    onSuccess: (quote) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.success(t('quotes.created'));
      navigate(`/quotes/${quote.id}`);
    },
  });
}

export function useUpdateQuote() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      const { data } = await api.put(`/quotes/${id}`, payload);
      return data.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quote', variables.id] });
      toast.success(t('quotes.updated'));
    },
  });
}

export function useUpdateQuoteStatus() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data } = await api.patch(`/quotes/${id}/status`, { status });
      return data.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quote', variables.id] });
      toast.success(t('quotes.statusUpdated'));
    },
  });
}

export function useConvertQuote() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/quotes/${id}/convert`);
      return data.data;
    },
    onSuccess: (invoice) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.success(t('quotes.converted'));
      navigate(`/invoices/${invoice.id}`);
    },
  });
}

export function useDuplicateQuote() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      return data.data;
    },
    onSuccess: (quote) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.success(t('quotes.duplicated'));
      navigate(`/quotes/${quote.id}`);
    },
  });
}

export function useSendQuoteEmail() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      await api.post(`/quotes/${id}/send-email`, payload);
    },
    onSuccess: () => {
      toast.success(t('quotes.emailSent'));
    },
  });
}

export async function downloadQuotePdf(id: string, filename: string = 'quote.pdf') {
  const { setIsDownloading } = useUIStore.getState();
  try {
    setIsDownloading(true, 'Generating PDF... 0%');
    const { data } = await api.get(`/quotes/${id}/pdf`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setIsDownloading(true, `Generating PDF... ${percentage}%`);
        }
      },
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsDownloading(false);
  } catch (err) {
    setIsDownloading(false);
    throw err;
  }
}

export async function openQuotePdfForPrint(id: string) {
  const { setIsDownloading } = useUIStore.getState();
  try {
    setIsDownloading(true, 'Generating PDF... 0%');
    const { data } = await api.get(`/quotes/${id}/pdf`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setIsDownloading(true, `Generating PDF... ${percentage}%`);
        }
      },
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const printWindow = window.open(url);
    printWindow?.addEventListener('load', () => {
      setIsDownloading(false);
      printWindow.print();
    });
  } catch (err) {
    setIsDownloading(false);
    throw err;
  }
}

export function useSubmitQuoteComment() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({ id, message }: { id: string; message: string }) => {
      await api.post(`/quotes/${id}/comments`, { message });
    },
    onSuccess: () => {
      toast.success(t('quotes.commentSubmitted'));
    },
  });
}

export function useRejectQuote() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/quotes/${id}/reject`);
    },
    onSuccess: () => {
      toast.success(t('quotes.rejected'));
    },
  });
}

export function useAiEstimate() {
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/quotes/ai-estimate', payload);
      return { items: data.data, note: data.note };
    },
  });
}

export function useUploadQuoteImages() {
  return useMutation({
    mutationFn: async ({ quoteId, files }: { quoteId: string; files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('images[]', file);
      });
      const { data } = await api.post(`/quotes/${quoteId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
  });
}

export function useQuoteImages(quoteId: string) {
  return useQuery({
    queryKey: ['quote-images', quoteId],
    queryFn: async () => {
      const { data } = await api.get(`/quotes/${quoteId}/images`);
      return data.data;
    },
    enabled: !!quoteId,
  });
}

export function useDeleteQuoteImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ quoteId, filename }: { quoteId: string; filename: string }) => {
      const { data } = await api.delete(`/quotes/${quoteId}/images/${filename}`);
      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['quote-images', variables.quoteId] });
    },
  });
}

// ===== EXPENSE HOOKS =====
export function useExpenses(params = {}) {
  return useQuery({
    queryKey: ['expenses', params],
    queryFn: async () => {
      const { data } = await api.get('/expenses', { params });
      return data;
    },
  });
}

export function useCreateExpense() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/expenses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      toast.success(t('expenses.created'));
      navigate('/expenses');
    },
  });
}

export function useExpenseCategories() {
  return useQuery({
    queryKey: ['expenseCategories'],
    queryFn: async () => {
      const { data } = await api.get('/expenses/categories');
      return data.data;
    },
  });
}

export function useScanExpense() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/expenses/scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
  });
}

// ===== DOCUMENT HOOKS =====
export function useDocuments(params = {}) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: async () => {
      const { data } = await api.get('/documents', { params });
      return data;
    },
  });
}

export function useUploadDocument() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      toast.success(t('documents.uploaded'));
    },
  });
}

// ===== EMAIL HOOKS =====
export function useEmailLogs(params = {}) {
  return useQuery({
    queryKey: ['emailLogs', params],
    queryFn: async () => {
      const { data } = await api.get('/emails', { params });
      return data;
    },
  });
}

// ===== ACTIVITY HOOKS =====
export function useEntityActivity(entityType: string, entityId: string) {
  return useQuery({
    queryKey: ['activity', entityType, entityId],
    queryFn: async () => {
      const { data } = await api.get(`/activity/${entityType}/${entityId}`);
      return data.data;
    },
    enabled: !!entityType && !!entityId,
  });
}

export function useTeamMemberActivity(memberId: string) {
  return useQuery({
    queryKey: ['teamMemberActivity', memberId],
    queryFn: async () => {
      const { data } = await api.get(`/team/members/${memberId}/activity`);
      return data.data;
    },
    enabled: !!memberId,
  });
}

// ===== DESCRIPTION HOOKS =====
export function useLineDescriptions(search = '') {
  return useQuery({
    queryKey: ['lineDescriptions', search],
    queryFn: async () => {
      const { data } = await api.get('/accounting/line-descriptions', {
        params: { search },
      });
      return data.data;
    },
  });
}

// ===== ORGANIZATION HOOKS =====
export function useMyOrganizations() {
  return useQuery({
    queryKey: ['myOrganizations'],
    queryFn: async () => {
      const { data } = await api.get('/organizations/me');
      return data.data;
    },
  });
}

// ===== NOTIFICATION HOOKS =====
export function useNotificationCount() {
  return useQuery({
    queryKey: ['notificationCount'],
    queryFn: async () => {
      const { data } = await api.get('/notifications/count');
      return data.data;
    },
    refetchInterval: 30000,
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data } = await api.get('/notifications');
      return data.data;
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      await api.put(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notificationCount'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.put('/notifications/read-all');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notificationCount'] });
    },
  });
}

// ===== INVITATION HOOKS =====
export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data } = await api.post(`/invitations/${invitationId}/accept`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['myOrganizations'] });
    },
  });
}

export function useDeclineInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      await api.post(`/invitations/${invitationId}/decline`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ===== SUBSCRIPTION HOOKS =====
export function useSubscription() {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data } = await api.get('/subscription');
      return data.data;
    },
  });
}

// ===== DASHBOARD HOOKS =====
export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const { data } = await api.get('/dashboard');
      return data.data;
    },
  });
}

// ===== EXPORT HOOKS =====
export function useExport() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({ type, params }: { type: string; params: any }) => {
      const { data } = await api.get(`/export/${type}`, {
        params,
        responseType: 'blob',
      });
      return data;
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${variables.type}-${new Date().toISOString()}.csv`;
      a.click();
      toast.success(t('common.exported'));
    },
  });
}

// ===== QUOTA HOOKS =====
export function useQuotaStatus() {
  const query = useQuery({
    queryKey: ['quotaStatus'],
    queryFn: async () => {
      const { data } = await api.get('/subscription/quota/status');
      return data.data;
    },
  });

  // Return data with safe defaults so components can access properties directly
  return query.data || {
    isLoaded: false,
    planLabel: 'Free',
    plan: 'free',
    canUseAiEstimate: false,
    canCreateInvoice: true,
    canCreateClient: true,
    invoicesUsed: 0,
    invoicesLimit: null,
    invoicesRemaining: null,
    clientsUsed: 0,
    clientsLimit: null,
    clientsRemaining: null,
  };
}

// ===== VAT REPORT HOOKS =====
export function useVatReports(params = {}) {
  return useQuery({
    queryKey: ['vatReports', params],
    queryFn: async () => {
      const { data } = await api.get('/reports/vat', { params });
      return data;
    },
  });
}

export function useGenerateVatReport() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/reports/vat', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vatReports'] });
      toast.success(t('reports.generated'));
    },
  });
}

// ===== API KEY HOOKS =====
export function useApiKeys() {
  return useQuery({
    queryKey: ['apiKeys'],
    queryFn: async () => {
      const { data } = await api.get('/api-keys');
      return data.data;
    },
  });
}

export function useApiScopes() {
  return useQuery({
    queryKey: ['apiScopes'],
    queryFn: async () => {
      const { data } = await api.get('/api-keys/scopes');
      return data.data;
    },
  });
}

export function useCreateApiKey() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api-keys', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apiKeys'] });
      toast.success(t('apiKeys.created'));
    },
  });
}

export function useRevokeApiKey() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api-keys/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apiKeys'] });
      toast.success(t('apiKeys.revoked'));
    },
  });
}

export function useUpdateApiKeyScopes() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scopes }: { id: string; scopes: string[] }) => {
      const { data } = await api.put(`/api-keys/${id}/scopes`, { scopes });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apiKeys'] });
      toast.success(t('apiKeys.scopesUpdated'));
    },
  });
}

// ===== SUBSCRIPTION PLAN HOOKS =====
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: async () => {
      const { data } = await api.get('/subscription/plans');
      return data.data;
    },
  });
}

// ===== PLATFORM ANNOUNCEMENTS (dashboard banner) =====
export function useActiveAnnouncements() {
  return useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: async () => {
      const { data } = await api.get('/announcements/active');
      return data.data as PlatformAnnouncement[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: async (payload: { plan: string; interval: string }) => {
      const { data } = await api.post('/subscription/checkout', payload);
      return data.data;
    },
  });
}

export function useCreateBillingPortal() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/subscription/billing-portal');
      return data.data;
    },
    onSuccess: (data) => {
      window.location.href = data.portalUrl;
    },
  });
}

export function useCancelSubscription() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/subscription/cancel');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription'] });
      toast.success(t('subscription.cancelled'));
    },
  });
}

export function useResumeSubscription() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/subscription/resume');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription'] });
      toast.success(t('subscription.resumed'));
    },
  });
}

export function useVerifyCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkoutId: string) => {
      const { data } = await api.post('/subscription/verify-checkout', { checkoutId });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

// ===== TEAM HOOKS =====
export function useTeamActivity() {
  return useQuery({
    queryKey: ['teamActivity'],
    queryFn: async () => {
      const { data } = await api.get('/team/activity');
      return data.data;
    },
  });
}

// ===== BRANDING HOOKS =====
export function useBranding() {
  return useQuery({
    queryKey: ['branding'],
    queryFn: async () => {
      const { data } = await api.get('/settings/branding');
      return data.data;
    },
  });
}

export function useUpdateBranding() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { colors?: any; layout?: any; fontFamily?: string }) => {
      const { data } = await api.post('/settings/branding', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding'] });
      toast.success(t('branding.saved'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });
}

export function useUploadBrandingLogo() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await api.post('/settings/branding/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding'] });
      toast.success(t('branding.logoUploaded'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });
}

export function useDeleteBrandingLogo() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete('/settings/branding/logo');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding'] });
      toast.success(t('branding.logoRemoved'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });
}

// ===== TERMS & CONDITIONS HOOKS =====
export function useTermsAndConditions() {
  return useQuery({
    queryKey: ['termsAndConditions'],
    queryFn: async () => {
      const { data } = await api.get('/settings/terms');
      return data.data;
    },
  });
}

export function useTermsAndCondition(id: string) {
  return useQuery({
    queryKey: ['termsAndCondition', id],
    queryFn: async () => {
      const { data } = await api.get(`/settings/terms/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useCreateTermsAndConditions() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/settings/terms', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['termsAndConditions'] });
      toast.success(t('terms.created'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });
}

export function useUpdateTermsAndConditions(id: string) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.put(`/settings/terms/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['termsAndConditions'] });
      qc.invalidateQueries({ queryKey: ['termsAndCondition', id] });
      toast.success(t('terms.updated'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });
}

export function useDeleteTermsAndConditions(id: string) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/settings/terms/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['termsAndConditions'] });
      toast.success(t('terms.deleted'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });
}
